import type { SynergyFigure } from '../../synergy-risk';

/**
 * Wave C0 (#8990, founder 2026-10-01 13:12 PT): the executive findings, on screen. One panel per
 * typed executive-finding.v1 finding, projected server-side. Every sentence is server-authored,
 * every figure is carried from the finding's exact engine figure (its pointer kept as evidence),
 * and a modeled figure is `certainty: 'estimated'`. The web draws these panels as given.
 */
export const ASK_VIEW_EXECUTIVE_SLOT_KEYS = [
  'whatHappened', 'why', 'howMaterial', 'whatItMeans', 'whatWeCanDo', 'whatItIsWorth',
  'whatCouldGoWrong',
] as const;
export type AskViewExecutiveSlotKey = (typeof ASK_VIEW_EXECUTIVE_SLOT_KEYS)[number];

/**
 * One figure with what it measures (a cause, a plan line). `after` puts the label after the
 * figure ("80% of the $600K gross shortfall explained"); otherwise it leads ("Licences $200K").
 */
export type AskViewExecutiveEntry = Readonly<{
  label: string;
  after: boolean;
  figure: SynergyFigure;
}>;

/** One answered so-what slot: every figure in it is labelled. */
export type AskViewExecutiveSlot = Readonly<{
  key: AskViewExecutiveSlotKey;
  label: string;
  entries: readonly AskViewExecutiveEntry[];
  /** Set on the answered slots the applied lens puts first (#8990 C2b). */
  leads?: true;
}>;

export type AskViewExecutiveSoWhat = Readonly<{
  kind: 'so_what';
  findingId: string;
  title: string;
  /** Answered slots only, in the applied lens's declared order (canonical without one). */
  slots: readonly AskViewExecutiveSlot[];
  /** One muted line naming the slots not yet assessed; null when all seven are answered. */
  notYetAssessed: string | null;
  /** #10230: one server-authored disclosure line, e.g. that the drawn parts do not foot. */
  note?: string;
  departmentSplit?: Readonly<{ total: SynergyFigure; entries: readonly AskViewExecutiveEntry[] }>;
}>;

export type AskViewExecutiveChallenge = Readonly<{
  kind: 'challenge';
  findingId: string;
  title: string;
  claim: string;
  severity: 'high' | 'medium' | 'low';
  atRisk: SynergyFigure | null;
  resolvesWhen: string;
}>;

export type AskViewExecutiveCounterfactual = Readonly<{
  kind: 'counterfactual';
  findingId: string;
  title: string;
  summary: string;
  foregone: Readonly<{ low: SynergyFigure; base: SynergyFigure; high: SynergyFigure }>;
  costPerMonthOfDelay: SynergyFigure;
}>;

export type AskViewExecutiveCommitmentStatus = Readonly<{
  kind: 'commitment_status';
  findingId: string;
  title: string;
  /** Only what the commitment page does not already show, each row labelled by its measure. */
  rows: readonly AskViewExecutiveEntry[];
  forecastBasis?: string;
  landingMissing?: string;
  landing?: Readonly<{ promise: SynergyFigure; forecast: SynergyFigure;
    cases?: Readonly<Record<'downside' | 'base' | 'upside', SynergyFigure>>; by?: string;
    difference: SynergyFigure | null; relation: 'short' | 'above' | 'on_promise';
    finishLabel: string }>;
}>;

export type AskViewExecutivePanel =
  | AskViewExecutiveSoWhat
  | AskViewExecutiveChallenge
  | AskViewExecutiveCounterfactual
  | AskViewExecutiveCommitmentStatus;
